//******************************************************************************
/**
 * @fileoverview RangeChunkFetcher Rangeリクエストでファイルを分割取得するクラス
 * @author KITAO Kaoru <kaoru@kitao.net>
 * @generated ChatGPT
 * @version 0.1.5
 * @since 2026-01-01
 * @copyright 2026 KITAO Kaoru
 * @license MIT
 * * @history
 * - 2026.02.27 v0.1.5 空行削除・package.json堅牢化
 * - 2026.02.21 v0.1.4 fetch調整によるメモリ割り当ての最小化
 * - 2026.02.04 v0.1.3 ChatGPTによる補完と調整
 * - 2026.02.03 V0.1.2 CORS対応追加
 * - 2026.01.01 v0.1.0 リリース
 */

//******************************************************************************
/**
 * Range分割でファイルを取得するクラス
 */
class RangeChunkFetcher {
	//**************************************************************************
	/**
	 * コンストラクタ
	 * @param {number} [split=8] ファイルの最大分割数
	 * @param {number} [timeoutMs=15000] 各リクエストのタイムアウト時間（ミリ秒）
	 * @param {number} [retries=3] 各チャンクの最大リトライ回数
	 */
	constructor(split = 8, timeoutMs = 15000, retries = 3) {
		this.split = split;
		this.timeoutMs = timeoutMs;
		this.retries = retries;
		this.MB = 1024 * 1024;
	}

	//**************************************************************************
	/**
	 * Headのfetch
	 * @param {string} url フェッチ対象のURL
	 * @returns {Promise<Response>} fetchのResponseオブジェクト
	 * @throws {Error} タイムアウト時、またはfetch失敗時
	 */
	async fetchHead(url) {
		for (let attempt = 0; attempt < 2; attempt += 1) {
			try {
				const response = await this.fetchWithTimeout(url, {
					method: "HEAD"
				});
				if (response.ok === false) {
					throw new Error(`HTTP ${response.status}`);
				}
				return response;
			} catch (error) {
				if (attempt === 1) {
					throw error;
				}
			}
		}
	}

	//**************************************************************************
	/**
	 * タイムアウト付きfetchを実行
	 * - 内部でAbortControllerを生成して指定時間経過後にリクエストを中断
	 * - タイムアウト時はErrorをthrowする
	 * @param {string} url フェッチ対象のURL
	 * @param {RequestInit} [options={}] fetchに渡すオプション
	 * @returns {Promise<Response>} fetchのResponseオブジェクト
	 * @throws {Error} タイムアウト時、またはfetch失敗時
	 */
	async fetchWithTimeout(url, options = {}) {
		const controller = new AbortController();
		const timer = setTimeout(() => {
			controller.abort();
		}, this.timeoutMs);
		try {
			const response = await fetch(url, {
				...options,
				mode: "cors",
				signal: controller.signal
			});
			return response;
		} catch (error) {
			if (error.name === "AbortError") {
				throw new Error(`Timeout after ${this.timeoutMs} ms.`);
			}
			throw error;
		} finally {
			clearTimeout(timer);
		}
	}

	//**************************************************************************
	/**
	 * 指定されたバイト範囲のチャンクを取得
	 *   タイムアウト・通信エラー時は設定された回数までリトライする。
	 * @param {string} url フェッチ対象のURL
	 * @param {number} start 取得開始バイト位置
	 * @param {number} end 取得終了バイト位置
	 * @returns {Promise<ArrayBuffer>} 取得したチャンクデータ
	 * @throws {Error} リトライ上限を超えて失敗した場合
	 */
	async fetchChunk(url, start, end) {
		for (let attempt = 0; attempt <= this.retries; attempt += 1) {
			try {
				const response = await this.fetchWithTimeout(url, {
					headers: {
						Range: `bytes=${start}-${end}`
					}
				});
				if (response.ok === false) {
					throw new Error(`HTTP ${response.status}`);
				}
				return await response.arrayBuffer();
			} catch (error) {
				if (attempt === this.retries) {
					throw new Error(
						`Chunk failed after ${this.retries + 1} attempts: ${error.message}`
					);
				}
			}
		}
	}

	//**************************************************************************
	/**
	 * 複数のArrayBufferを1つに結合する
	 * @param {ArrayBuffer[]} buffers 結合対象のArrayBuffer配列（順序保持）
	 * @returns {ArrayBuffer} 結合後のArrayBuffer
	 */
	concat(buffers) {
		const total = buffers.reduce((acc, cur) => acc + cur.byteLength, 0);
		const typed = new Uint8Array(total);
		let offset = 0;
		for (const buffer of buffers) {
			typed.set(new Uint8Array(buffer), offset);
			offset += buffer.byteLength;
		}
		return typed.buffer;
	}

	//**************************************************************************
	/**
	 * ファイル全体をRange分割で取得
	 * @param {string} url 取得対象ファイルのURL
	 * @returns {Promise<ArrayBuffer>} 取得したファイル全体のArrayBuffer
	 * @throws {Error} HEAD失敗時
	 * @throws {Error} サイズ取得不可時
	 * @throws {Error} チャンク取得失敗時
	 */
	async fetch(url) {
		const head = await this.fetchHead(url);
		if (head.ok === false) {
			throw new Error(`HEAD request failed: ${head.status}`);
		}
		const size = Number(head.headers.get("Content-Length"));
		if (Number.isFinite(size) === false) {
			throw new Error("Content-Length is missing");
		}
		let split = this.split;
		for (let i = 1; i <= this.split; i += 1) {
			if (size < ((i ** 2) * this.MB)) {
				split = i;
				break;
			}
		}
		const chunkSize = Math.ceil(size / split);
		const promises = [];
		// for (let i = 0; i < this.split; i += 1) {
		for (let i = 0; i < split; i += 1) {
			const start = i * chunkSize;
			const end = Math.min(start + chunkSize - 1, size - 1);
			promises.push(this.fetchChunk(url, start, end));
		}
		// return this.concat(await Promise.all(promises));
		// 全チャンクの取得完了を待機
		const chunks = await Promise.all(promises);
		// 結合処理をメソッド内に展開
		const result = new Uint8Array(size);
		let offset = 0;
		for (const chunk of chunks) {
			// fetchChunkがArrayBufferを返す場合はnew Uint8Array(chunk)が必要
			// すでにUint8Arrayを返しているならそのままset可能
			result.set(new Uint8Array(chunk), offset);
			offset += chunk.byteLength;
		}
		return result.buffer;
	}
}

//******************************************************************************
// export
export { RangeChunkFetcher };
